import 'locomotive-scroll/dist/locomotive-scroll.css';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { LocomotiveScrollProvider } from 'react-locomotive-scroll';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import {
  handleIsLoading,
  scrollDestroySelector,
  scrollUpdateSelector,
  videoModalSelector,
} from '../entities/ComponentsSlice';
import { defineDevice, handleNewUser } from '../entities/UserSlice';
import VideoModal from '../features/video-modal/VideoModal';
import LoaderCircle from '../shared/loader-circle/LoaderCircle';
import { handleBodyLock } from '../tools/_handle-body-lock';
import { handleBodyUnlock } from '../tools/_handle-body-unlock';
import ScrollTriggerProxy from '../tools/helpers/ScrollTriggerProxy';
import useModalService from '../tools/hooks/useModalsService';
import { useUserService } from '../tools/hooks/useUserService';
import CookiePopup from '../widgets/cookie-popup/CookiePopup';
import Footer from '../widgets/footer/Footer';
import FormModal from '../widgets/form-modal/FormModal';
import Header from '../widgets/header/Header';
import MenuMobile from '../widgets/menu-mobile/MenuMobile';
import ModalEmail from '../widgets/modal-email/ModalEmail';
import ModalError from '../widgets/modal-error/ModalError';
import ModalMenu from '../widgets/modal-menu/ModalMenu';
import ROUTES from './routes';

const PanoramaModal = lazy(() => import('../widgets/panorama-modal/PanoramaModal'));

function getCookie(cname) {
  const name = cname + '=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    while (ca[i].charAt(0) === ' ') {
      ca[i] = ca[i].slice(1);
    }
    if (ca[i].indexOf(name) === 0) {
      return ca[i].slice(name.length, ca[i].length);
    }
  }
  return '';
}

function Layout({ width, userAgent, loaded, setLoaded }) {
  const dispatch = useDispatch();
  setTimeout(() => {
    dispatch(handleIsLoading(true));
  }, 300);

  useEffect(() => {
    if (width) {
      dispatch(defineDevice({ width, userAgent }));
    }
  }, [width, dispatch]);

  const { modelError, modelForm, menu, menuMobile, emailForm, filterMenu, panoramaModel } = useModalService();

  useEffect(() => {
    if (modelError || modelForm || menu || menuMobile || emailForm || filterMenu || panoramaModel) {
      handleBodyLock();
    } else {
      handleBodyUnlock();
    }
  }, [modelError, modelForm, menu, menuMobile, emailForm, filterMenu, panoramaModel]);

  const containerRef = useRef(null);

  const { pathname } = useLocation();

  const { device, useragent, orientation } = useUserService();
  const isANewUser = useSelector(state => state.userinfo.isANewUser);
  const scrollDestroy = useSelector(scrollDestroySelector);

  useEffect(() => {}, [device, useragent, orientation]);

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {
    if (!getCookie('moments_user')) return;
    dispatch(handleNewUser(true));
  }, [isANewUser]);

  const scrollUpdate = useSelector(scrollUpdateSelector);
  const videoModal = useSelector(videoModalSelector);

  return (
    <>
      {!isMobileOnly && (
        <LocomotiveScrollProvider
          options={{
            smooth: true,
            smartphone: {
              smooth: false,
            },
            tablet: {
              smooth: true,
            },
            resetNativeScroll: false,
          }}
          watch={[scrollUpdate]}
          location={pathname}
          containerRef={containerRef}
          onLocationChange={scroll => {
            scroll.scrollTo(0, { duration: 0, disableLerp: true });
            ScrollTrigger.update();
            if (pathname !== ROUTES.root)
              //2 сек пауза только для главной
              return;
            setLoaded(false);
            setTimeout(() => {
              setLoaded(true);
            }, 2000);
          }} // If you want to reset the scroll position to 0 for example
          onUpdate={() => {}}
        >
          <ScrollTriggerProxy />
          <div
            data-scroll-container
            data-scroll-direction='vertical'
            id='fixed-target'
            ref={containerRef}
            className={`App ${loaded ? '' : 'App--blocked'} ${!scrollDestroy ? '' : 'App--scroll'}`}
          >
            <div className='target' />
            <Header />
            {!isANewUser && <CookiePopup />}
            <Outlet />
            <Footer />
          </div>
          {menuMobile && <MenuMobile />}
          <Suspense fallback={<LoaderCircle />}>{panoramaModel && <PanoramaModal />}</Suspense>
          <ModalEmail />
          <ModalError />
          <FormModal />
          <ModalMenu />
          {videoModal && <VideoModal />}
        </LocomotiveScrollProvider>
      )}
    </>
  );
}

export default Layout;
